import React, { useEffect, useState } from 'react';
import { useEnv } from 'src/context/EnvProvider';
import useLoadDataList from 'src/hook/useLoadDataList';
import { useTexture } from 'src/hook/useTexture';
import { Granit } from 'src/services/granits';
import { getPiece } from 'src/utils/configurator.utils';
import {
  getIndexType,
  isTwoSteleEqual,
  getDimensionsWithGeometry,
  getGlobalIndex,
} from 'src/utils/3d.utils';
import {
  getAssemblyStockWithoutGranit,
  getStocksByPieceId,
  getStocksByStele,
} from 'src/services/stock';
import { TexturesProps, SortType } from './types';
import {
  useActiveGranits,
  useFilteredGranits,
  useSortedGranits,
} from './hooks';
import {
  changeTexture,
  selectedTexture,
  selectPieceWithGranitId,
  applyStockGranits,
  StockToApply,
} from './utils';
import { TextureSelection } from './TextureSelection';
import { GranitCard } from './GranitCard';
import { StockModal } from './StockModal';
import { AdvancedMode } from 'src/components/3d/Fragments/AdvancedMode';
import { StateNames } from 'src/Screens/Configurator';

export const Textures = ({
  activePieces,
  setActivePieces,
  pieceIndex,
  customSteles,
  setCustomSteles,
  customPlatings,
  setCustomPlatings,
  setPieceIndex,
  getValue,
  pieces,
  setValue,
  customSoles,
  setCustomSoles,
  customBoxes,
  setCustomBoxes,
  history,
}: TexturesProps) => {
  const { buckets } = useEnv();
  const { granits } = useTexture();
  const [selectedDominant, setSelectedDominant] = useState<string>();
  const [hasSelectedAllPieces, setHasSelectedAllPieces] =
    useState<boolean>(false);
  const [filter, setFilter] = useState<string>('');
  const [error, setError] = useState<string>();
  const [sort, setSort] = useState<SortType>('ascending_name');
  const [categories, setCategories] = useState<string[]>([]);
  const [origin, setOrigin] = React.useState<string[]>([]);
  const [seeAssemblyStock, setSeeAssemblyStock] = useState<boolean>(false);
  const [isStockModalOpen, setIsStockModalOpen] = useState<boolean>(false);

  const type = getIndexType(
    pieceIndex,
    activePieces,
    customSteles,
    customPlatings,
    customSoles,
    customBoxes,
  );
  const piece = getPiece(
    pieceIndex,
    pieces,
    customSteles,
    customPlatings,
    customSoles,
    customBoxes,
  );

  // Sync selectedDominant when the current piece's granit changes
  useEffect(() => {
    if (
      selectedDominant &&
      activePieces[pieceIndex]?.granit?.id !== selectedDominant
    ) {
      setSelectedDominant(activePieces[pieceIndex]?.granit?.id);
    }
  }, [selectedDominant, activePieces, pieceIndex]);

  // Create a stable key for piece geometry to avoid infinite re-renders
  const pieceGeometryKey = piece?.geometry?.attributes?.position?.array
    ? JSON.stringify(
        Array.from(piece.geometry.attributes.position.array.slice(0, 10)),
      )
    : '';

  const { data: stockData, onRefresh: refreshStock } = useLoadDataList(
    () =>
      getStocksByPieceId(
        piece.id,
        getDimensionsWithGeometry(piece?.geometry?.attributes?.position?.array),
      ),
    [piece.id, pieceGeometryKey],
    type === 'activePieces',
  );

  const { data: stockStelesData, onRefresh: refreshStockSteles } =
    useLoadDataList(
      () =>
        getStocksByStele({
          baseIndex: Number(piece.baseIndex),
          boxDownWidth: Number(piece.boxDownWidth),
          boxHeight: Number(piece.boxHeight),
          boxLeftHeight: Number(piece.boxLeftHeight),
          boxRightHeight: Number(piece.boxRightHeight),
          boxUpWidth: Number(piece.boxUpWidth),
          headIndex: Number(piece.headIndex),
          depth: Number(piece.depth),
        }),
      [piece.id],
      type === 'customStele',
    );

  // Create a stable key for pieces to avoid infinite re-renders
  const piecesKey = pieces.map((el) => el.id).join(',');

  const { data: assemblyStockData, onRefresh: refreshAssemblyStock } =
    useLoadDataList(
      () =>
        getAssemblyStockWithoutGranit({
          stockPieces: pieces.map((el) => {
            const { x, y, z } = getDimensionsWithGeometry(
              el.geometry.attributes.position.array,
            );

            return {
              pieceId: el.id ?? -1,
              newGeometry: [...el.geometry.attributes.position.array],
              x,
              y,
              z,
            };
          }),
        }),
      [piecesKey],
      type === 'activePieces',
    );

  useEffect(() => {
    if (type === 'activePieces') {
      refreshStock();
    }
  }, [piece.id, type]);

  const activeGranits = useActiveGranits(
    activePieces,
    customSteles,
    customPlatings,
    customSoles,
    customBoxes,
  );

  const filteredGranits = useFilteredGranits(
    granits,
    pieceIndex,
    activePieces,
    filter,
    origin,
    categories,
    seeAssemblyStock,
    assemblyStockData,
  );

  const sortedGranits = useSortedGranits(filteredGranits, sort);

  const handleChangeGranit = (granit: Granit) => {
    setError(undefined);
    if (selectedDominant) {
      const piecesIdsToChange: number[] = [];
      for (const [elementIndex, element] of activePieces.entries()) {
        if (element.granit?.id === selectedDominant) {
          if (element.underAssembly) {
            if (element.underAssembly.fabrications.includes(granit.fabrication))
              piecesIdsToChange.push(elementIndex);
          } else if (element.fabrications.includes(granit.fabrication)) {
            piecesIdsToChange.push(elementIndex);
          }
        }
      }
      for (const [elementIndex, element] of customSteles.entries()) {
        if (element.granit?.id === selectedDominant) {
          const globalIndex = getGlobalIndex(
            elementIndex,
            'customStele',
            activePieces,
            customSteles,
            customPlatings,
            customSoles,
            customBoxes,
          );

          piecesIdsToChange.push(globalIndex + elementIndex);
        }
      }
      for (const [elementIndex, element] of customPlatings.entries()) {
        if (element.granit?.id === selectedDominant) {
          const globalIndex = getGlobalIndex(
            elementIndex,
            'customPlating',
            activePieces,
            customSteles,
            customPlatings,
            customSoles,
            customBoxes,
          );

          piecesIdsToChange.push(globalIndex + elementIndex);
        }
      }
      for (const [elementIndex, element] of customSoles.entries()) {
        if (element.granit?.id === selectedDominant) {
          const globalIndex = getGlobalIndex(
            elementIndex,
            'customSole',
            activePieces,
            customSteles,
            customPlatings,
            customSoles,
            customBoxes,
          );

          piecesIdsToChange.push(globalIndex + elementIndex);
        }
      }
      for (const [elementIndex] of customBoxes.entries()) {
        const globalIndex = getGlobalIndex(
          elementIndex,
          'customBox',
          activePieces,
          customSteles,
          customPlatings,
          customSoles,
          customBoxes,
        );
        piecesIdsToChange.push(globalIndex + elementIndex);
      }

      changeTexture(
        granit,
        piecesIdsToChange,
        buckets ? buckets : { BUCKET_3D_PIECES: '' },
        activePieces,
        customSteles,
        customPlatings,
        customSoles,
        customBoxes,
        pieces,
        getValue('hasAdvancedMode'),
        setActivePieces,
        setCustomSteles,
        setCustomPlatings,
        setCustomSoles,
        setCustomBoxes,
        setError,
      );
      setSelectedDominant(granit.id);
    } else if (hasSelectedAllPieces) {
      const piecesIdsToChange: number[] = [];

      for (const [elementIndex, element] of activePieces.entries()) {
        if (
          element.fabrications?.includes(granit.fabrication) ||
          (element.underAssembly?.fabrications?.includes(granit.fabrication) &&
            element.material === granit.material)
        ) {
          piecesIdsToChange.push(elementIndex);
        }
      }
      for (const [elementIndex] of customSteles.entries()) {
        const globalIndex = getGlobalIndex(
          elementIndex,
          'customStele',
          activePieces,
          customSteles,
          customPlatings,
          customSoles,
          customBoxes,
        );

        piecesIdsToChange.push(globalIndex + elementIndex);
      }
      for (const [elementIndex] of customPlatings.entries()) {
        const globalIndex = getGlobalIndex(
          elementIndex,
          'customPlating',
          activePieces,
          customSteles,
          customPlatings,
          customSoles,
          customBoxes,
        );
        piecesIdsToChange.push(globalIndex + elementIndex);
      }
      for (const [elementIndex] of customSoles.entries()) {
        const globalIndex = getGlobalIndex(
          elementIndex,
          'customSole',
          activePieces,
          customSteles,
          customPlatings,
          customSoles,
          customBoxes,
        );
        piecesIdsToChange.push(globalIndex + elementIndex);
      }

      for (const [elementIndex] of customBoxes.entries()) {
        const globalIndex = getGlobalIndex(
          elementIndex,
          'customBox',
          activePieces,
          customSteles,
          customPlatings,
          customSoles,
          customBoxes,
        );
        piecesIdsToChange.push(globalIndex + elementIndex);
      }

      console.log('piecesIdsToChange', piecesIdsToChange);
      changeTexture(
        granit,
        piecesIdsToChange,
        buckets ? buckets : { BUCKET_3D_PIECES: '' },
        activePieces,
        customSteles,
        customPlatings,
        customSoles,
        customBoxes,
        pieces,
        getValue('hasAdvancedMode'),
        setActivePieces,
        setCustomSteles,
        setCustomPlatings,
        setCustomSoles,
        setCustomBoxes,
        setError,
      );
    } else {
      changeTexture(
        granit,
        [pieceIndex],
        buckets ? buckets : { BUCKET_3D_PIECES: '' },
        activePieces,
        customSteles,
        customPlatings,
        customSoles,
        customBoxes,
        pieces,
        getValue('hasAdvancedMode'),
        setActivePieces,
        setCustomSteles,
        setCustomPlatings,
        setCustomSoles,
        setCustomBoxes,
        setError,
      );
    }
  };

  const currentSelectedTexture = selectedTexture(
    pieceIndex,
    activePieces,
    customSteles,
    customPlatings,
    customSoles,
    customBoxes,
  );

  const handleSelectPieceWithGranitId = (granitId: string) => {
    selectPieceWithGranitId(
      granitId,
      activePieces,
      customSteles,
      customPlatings,
      customSoles,
      customBoxes,
      setPieceIndex,
    );
  };

  const handleApplyStock = (stock: StockToApply) => {
    setError(undefined);
    applyStockGranits(
      stock,
      granits,
      buckets ? buckets : { BUCKET_3D_PIECES: '' },
      activePieces,
      customSteles,
      customPlatings,
      customSoles,
      customBoxes,
      pieces,
      getValue('hasAdvancedMode'),
      setActivePieces,
      setCustomSteles,
      setCustomPlatings,
      setCustomSoles,
      setCustomBoxes,
      setError,
    );
    setIsStockModalOpen(false);
  };

  return (
    <div className='flex flex-col h-full'>
      {/* Toolbar Section */}
      <div className='px-5 py-3 bg-gray-50 border-b border-gray-100'>
        {/* Recherche et filtres sur une ligne */}
        <div className='flex items-center gap-3'>
          {/* Recherche */}
          <div className='flex-1 relative'>
            <svg
              className='absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400'
              fill='none'
              stroke='currentColor'
              viewBox='0 0 24 24'
            >
              <path
                strokeLinecap='round'
                strokeLinejoin='round'
                strokeWidth={2}
                d='M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z'
              />
            </svg>
            <input
              type='text'
              placeholder='Rechercher un granit...'
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              className='w-full pl-9 pr-3 py-2 text-sm bg-white border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-turquoise/30 focus:border-turquoise transition-all'
            />
          </div>

          {/* Catégories */}
          <select
            value={categories.length > 0 ? categories[0] : ''}
            onChange={(e) => {
              if (e.target.value === '') {
                setCategories([]);
              } else {
                setCategories([e.target.value]);
              }
            }}
            className='px-3 py-2 text-sm bg-white border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-turquoise/30 focus:border-turquoise transition-all cursor-pointer'
          >
            <option value=''>Toutes catégories</option>
            <option value='GRANIT'>Granit</option>
            <option value='MARBRE'>Marbre</option>
            <option value='PIERRE'>Pierre</option>
          </select>

          {/* Bouton Stock */}
          <button
            onClick={() => {
              refreshAssemblyStock();
              setIsStockModalOpen(true);
            }}
            className='flex items-center gap-2 px-3 py-2 text-sm font-medium text-turquoise-dark bg-turquoise-light hover:bg-turquoise/20 rounded-lg transition-colors'
          >
            <svg
              className='w-4 h-4'
              fill='none'
              stroke='currentColor'
              viewBox='0 0 24 24'
            >
              <path
                strokeLinecap='round'
                strokeLinejoin='round'
                strokeWidth={2}
                d='M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4'
              />
            </svg>
            <span>Stock</span>
          </button>
        </div>

        {/* Filtres secondaires */}
        <div className='flex items-center gap-4 mt-3'>
          {/* Tri */}
          <div className='flex items-center gap-2'>
            <span className='text-xs text-gray-500'>Trier:</span>
            <button
              onClick={() =>
                setSort(
                  sort === 'ascending_name'
                    ? 'descending_name'
                    : 'ascending_name',
                )
              }
              className={`inline-flex items-center gap-1 px-2.5 py-1 text-xs font-medium rounded-md transition-colors whitespace-nowrap ${
                sort.includes('name')
                  ? 'bg-turquoise text-white'
                  : 'bg-white text-gray-600 border border-gray-200 hover:bg-gray-50'
              }`}
            >
              <span>Nom</span>
              {sort.includes('name') && (
                <svg
                  className={`w-3 h-3 ${
                    sort === 'descending_name' ? 'rotate-180' : ''
                  }`}
                  fill='none'
                  stroke='currentColor'
                  viewBox='0 0 24 24'
                >
                  <path
                    strokeLinecap='round'
                    strokeLinejoin='round'
                    strokeWidth={2}
                    d='M5 15l7-7 7 7'
                  />
                </svg>
              )}
            </button>
            <button
              onClick={() =>
                setSort(
                  sort === 'ascending_price'
                    ? 'descending_price'
                    : 'ascending_price',
                )
              }
              className={`inline-flex items-center gap-1 px-2.5 py-1 text-xs font-medium rounded-md transition-colors whitespace-nowrap ${
                sort.includes('price')
                  ? 'bg-turquoise text-white'
                  : 'bg-white text-gray-600 border border-gray-200 hover:bg-gray-50'
              }`}
            >
              <span>Prix</span>
              {sort.includes('price') && (
                <svg
                  className={`w-3 h-3 ${
                    sort === 'descending_price' ? 'rotate-180' : ''
                  }`}
                  fill='none'
                  stroke='currentColor'
                  viewBox='0 0 24 24'
                >
                  <path
                    strokeLinecap='round'
                    strokeLinejoin='round'
                    strokeWidth={2}
                    d='M5 15l7-7 7 7'
                  />
                </svg>
              )}
            </button>
          </div>

          {/* Origine */}
          <div className='flex items-center gap-2'>
            <span className='text-xs text-gray-500'>Origine:</span>
            <button
              onClick={() => {
                if (origin.includes('FRENCH')) {
                  setOrigin(origin.filter((el) => el !== 'FRENCH'));
                } else {
                  setOrigin([...origin, 'FRENCH']);
                }
              }}
              className={`px-2.5 py-1 text-xs font-medium rounded-md transition-colors ${
                origin.includes('FRENCH')
                  ? 'bg-turquoise text-white'
                  : 'bg-white text-gray-600 border border-gray-200 hover:bg-gray-50'
              }`}
            >
              Français
            </button>
            <button
              onClick={() => {
                if (origin.includes('IMPORT')) {
                  setOrigin(origin.filter((el) => el !== 'IMPORT'));
                } else {
                  setOrigin([...origin, 'IMPORT']);
                }
              }}
              className={`px-2.5 py-1 text-xs font-medium rounded-md transition-colors ${
                origin.includes('IMPORT')
                  ? 'bg-turquoise text-white'
                  : 'bg-white text-gray-600 border border-gray-200 hover:bg-gray-50'
              }`}
            >
              Import
            </button>
          </div>
        </div>
      </div>

      {/* Grille de granits */}
      <div
        className='flex-1 overflow-y-auto p-5'
        style={{ maxHeight: 'calc(100vh - 400px)' }}
      >
        {/* Header Section */}
        <div className='flex flex-col mb-4'>
          {/* Mode avancé */}
          <div className='flex justify-end w-fit'>
            <AdvancedMode
              getValue={getValue}
              setValue={setValue}
              reverse={true}
            />
          </div>

          {/* Message d'erreur ou info */}
          {error && (
            <div className='mt-3 px-3 py-2 bg-red-50 border border-red-200 rounded-lg'>
              <p className='text-xs font-medium text-red-600'>{'error'}</p>
            </div>
          )}
        </div>
        {/* Sélection de pièces */}
        <div className='flex flex-wrap gap-2 items-center mb-4'>
          <TextureSelection
            hasSelectedAllPieces={hasSelectedAllPieces}
            setHasSelectedAllPieces={setHasSelectedAllPieces}
            activeGranits={activeGranits}
            selectedDominant={selectedDominant}
            setSelectedDominant={setSelectedDominant}
            buckets={buckets ? buckets : { BUCKET_3D_PIECES: '' }}
            activePieces={activePieces}
            pieceIndex={pieceIndex}
            setValue={(stateName: string, value: any) =>
              setValue(stateName as StateNames, value)
            }
            selectPieceWithGranitId={handleSelectPieceWithGranitId}
          />
        </div>
        {sortedGranits.length > 0 ? (
          <div className='grid grid-cols-2 lg:grid-cols-3 gap-4'>
            {sortedGranits.map((el, index) => {
              let hasStock = false;

              if (type === 'customStele') {
                hasStock = !!stockStelesData?.find((stock) => {
                  return (
                    isTwoSteleEqual(stock.stele, piece) &&
                    stock.granitId === el.id
                  );
                });
              } else {
                hasStock = !!stockData?.find(
                  (stock) => stock.granitId === el.id,
                );
              }

              if (seeAssemblyStock) hasStock = true;

              return (
                <div className='items-center justify-center flex'>
                  <GranitCard
                    key={index}
                    granit={el}
                    imageUrl={`${buckets?.BUCKET_3D_PIECES || ''}/${el.image}`}
                    isSelected={currentSelectedTexture?.name === el.name}
                    hasStock={hasStock}
                    onClick={() => handleChangeGranit(el)}
                  />
                </div>
              );
            })}
          </div>
        ) : (
          <div className='flex flex-col items-center justify-center h-48 text-gray-400'>
            <svg
              className='w-12 h-12 mb-3'
              fill='none'
              stroke='currentColor'
              viewBox='0 0 24 24'
            >
              <path
                strokeLinecap='round'
                strokeLinejoin='round'
                strokeWidth={1.5}
                d='M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z'
              />
            </svg>
            <p className='text-sm font-medium'>Aucun granit disponible</p>
            <p className='text-xs mt-1'>Essayez de modifier vos filtres</p>
          </div>
        )}
      </div>

      {/* Modal Stock */}
      <StockModal
        isOpen={isStockModalOpen}
        onClose={() => setIsStockModalOpen(false)}
        stockData={assemblyStockData}
        onApplyStock={handleApplyStock}
        bucketUrl={buckets?.BUCKET_3D_PIECES || ''}
      />
    </div>
  );
};
